import { gatewayApi as api } from './gatewayApi';

// Interfaces
export interface Client {
  Id?: number;
  Name: string;
  Description?: string;
  Active: boolean;
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface ClientListResponse {
  data: Client[];
  total: number;
}

export interface ClientSearchParams {
  page?: number;
  pageSize?: number;
  search?: string;
  active?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// API Service
export const clientApi = {
  // CRUD Operations
  getAll: (params?: ClientSearchParams): Promise<ClientListResponse> =>
    api.get('/client', params),

  getById: (id: number): Promise<Client> =>
    api.get(`/client/${id}`),

  create: (client: Omit<Client, 'Id'>): Promise<Client> =>
    api.post('/client', client),

  update: (id: number, client: Partial<Client>): Promise<Client> =>
    api.put(`/client/${id}`, client),

  delete: (id: number): Promise<{ success: boolean; message: string }> =>
    api.delete(`/client/${id}`),

  // Search and Filter
  search: (params: ClientSearchParams): Promise<ClientListResponse> =>
    api.post('/client/search', params),

  // Bulk Operations
  bulkActivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/client/bulk-activate', { ids }),

  bulkDeactivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/client/bulk-deactivate', { ids }),

  bulkDelete: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.post('/client/bulk-delete', { ids }),

  // Statistics
  getStatistics: (): Promise<{
    total: number;
    active: number;
    inactive: number;
  }> =>
    api.get('/client/statistics'),

  // Export
  exportToCSV: (params?: ClientSearchParams): Promise<Blob> =>
    api.getBlob('/client/export', { params }),

  // Validation
  validation: {
    validateClient: (client: Partial<Client>): string[] => {
      const errors: string[] = [];

      // Name validation
      if (!client.Name || client.Name.trim().length === 0) {
        errors.push('Client Name is required');
      } else if (client.Name.trim().length < 2) {
        errors.push('Client Name must be at least 2 characters long');
      } else if (client.Name.trim().length > 100) {
        errors.push('Client Name must be less than 100 characters');
      }

      // Description validation (optional but if provided, validate length)
      if (client.Description && client.Description.trim().length > 500) {
        errors.push('Description must be less than 500 characters');
      }

      // Active validation
      if (client.Active === undefined || client.Active === null) {
        errors.push('Active status is required');
      }

      return errors;
    },

    validateBulkOperation: (ids: number[]): string[] => {
      const errors: string[] = [];

      if (!ids || ids.length === 0) {
        errors.push('At least one Client must be selected');
      }

      if (ids.length > 100) {
        errors.push('Cannot perform bulk operation on more than 100 items at once');
      }

      return errors;
    }
  }
};

export default clientApi; 